import { Controller } from "@hotwired/stimulus"

/**
 * Inline Edit Controller
 *
 * Supports three modes:
 * 1. Toggle mode: Shows/hides label and input container (original behavior)
 * 2. Contenteditable mode: Makes element editable in place and saves via AJAX
 * 3. Textarea mode: Swaps display for a textarea (supports text-expander mentions)
 *
 * Mode is auto-detected: textarea target present → textarea mode, else contenteditable.
 */
export default class extends Controller {
  static targets = ["label", "inputContainer", "display", "textarea", "textareaContainer"]
  static values = {
    url: String,
    param: String,
    extraParams: String
  }

  // ==========================================
  // Toggle mode (original behavior)
  // ==========================================

  toggle(event) {
    event.preventDefault()

    this.labelTargets.concat(this.inputContainerTargets).forEach((element) => {
      element.classList.toggle('hidden')
    })

    this.inputContainerTargets.forEach((element) => {
      const inputElement = element.querySelector("input") || element.querySelector("select")

      if (inputElement && inputElement.nodeName === 'SELECT') {
        // Not possible at the moment
      } else if (inputElement && inputElement.nodeName === 'INPUT') {
        inputElement.focus()
        this._moveCursorToEnd(inputElement)
      }
    })

    const form = event.target.closest("form")
    if (form) {
      const editModeEvent = new CustomEvent("edit-mode", { detail: { formId: form.id } })
      window.dispatchEvent(editModeEvent)
    }
  }

  _moveCursorToEnd(element) {
    if (typeof element.selectionStart === "number") {
      element.selectionStart = element.selectionEnd = element.value.length
    } else if (typeof element.createTextRange !== "undefined") {
      element.focus()
      let range = element.createTextRange()
      range.collapse(false)
      range.select()
    }
  }

  // ==========================================
  // Contenteditable mode (new behavior)
  // ==========================================

  edit(event) {
    // Textarea mode: toggle between display and textarea
    if (this.hasTextareaTarget) {
      if (!this.displayTarget.hidden) {
        this._enterTextareaMode()
      }
      return
    }

    const displayElement = this.displayTarget

    // Already in edit mode — let the event through (e.g. space key triggers click)
    if (displayElement.contentEditable === "true") return

    event.preventDefault()
    event.stopPropagation()
    const originalText = displayElement.textContent.trim()

    // Store original text for potential cancel
    displayElement.dataset.originalText = originalText

    // Make editable
    displayElement.contentEditable = "true"
    displayElement.classList.add("outline-none", "!border-blue-500")

    // Select all text
    const range = document.createRange()
    range.selectNodeContents(displayElement)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)

    // Focus
    displayElement.focus()

    // Add event listeners
    displayElement.addEventListener("blur", this._onBlur)
    displayElement.addEventListener("keydown", this._onKeydown)
  }

  // ==========================================
  // Textarea mode
  // ==========================================

  _enterTextareaMode() {
    const textarea = this.textareaTarget
    this.displayTarget.hidden = true
    this.textareaContainerTarget.hidden = false
    textarea.focus()
    textarea.selectionStart = textarea.selectionEnd = textarea.value.length
    this._autoGrowTextarea(textarea)

    textarea.addEventListener("keydown", this._onTextareaKeydown)
    textarea.addEventListener("blur", this._onTextareaBlur)
  }

  _exitTextareaMode() {
    clearTimeout(this._blurTimeout)
    const textarea = this.textareaTarget
    textarea.removeEventListener("keydown", this._onTextareaKeydown)
    textarea.removeEventListener("blur", this._onTextareaBlur)
    this.textareaContainerTarget.hidden = true
    this.displayTarget.hidden = false
  }

  _onTextareaBlur = () => {
    // Delay to allow text-expander mention selection to refocus the textarea
    this._blurTimeout = setTimeout(() => {
      if (!this._savingTextarea && document.activeElement !== this.textareaTarget) {
        this._saveTextarea()
      }
    }, 150)
  }

  _onTextareaKeydown = (event) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      event.stopPropagation()
      this._saveTextarea()
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this._exitTextareaMode()
    }
  }

  async _saveTextarea() {
    if (this._savingTextarea) return
    this._savingTextarea = true
    const textarea = this.textareaTarget
    const newText = textarea.value.trim()
    const displayElement = this.displayTarget
    const originalText = displayElement.dataset.originalText || ""

    if (newText === originalText) {
      this._exitTextareaMode()
      this._savingTextarea = false
      return
    }

    displayElement.dataset.originalText = newText
    await this._saveValue(newText)
    this._exitTextareaMode()
    this._savingTextarea = false
  }

  _autoGrowTextarea(textarea) {
    textarea.style.height = "auto"
    textarea.style.height = textarea.scrollHeight + "px"
  }

  _onBlur = async (event) => {
    const displayElement = event.target
    this._removeEditMode(displayElement)
    await this._saveFromContenteditable(displayElement)
  }

  _onKeydown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      event.target.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      const displayElement = event.target
      displayElement.textContent = displayElement.dataset.originalText
      this._removeEditMode(displayElement)
    }
  }

  _removeEditMode(displayElement) {
    displayElement.contentEditable = "false"
    displayElement.classList.remove("outline-none", "!border-blue-500")
    displayElement.removeEventListener("blur", this._onBlur)
    displayElement.removeEventListener("keydown", this._onKeydown)
  }

  async _saveFromContenteditable(displayElement) {
    const newText = displayElement.textContent.trim()
    const originalText = displayElement.dataset.originalText

    // Don't save if unchanged or empty
    if (newText === originalText || newText === "") {
      if (newText === "") {
        displayElement.textContent = originalText
      }
      return
    }

    const success = await this._saveValue(newText)
    if (!success) {
      displayElement.textContent = originalText
    }
  }

  async _saveValue(newText) {
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: `${encodeURIComponent(this.paramValue)}=${encodeURIComponent(newText)}${this.hasExtraParamsValue ? '&' + this.extraParamsValue : ''}`
      })

      if (response.ok) {
        const html = await response.text()
        if (html) {
          Turbo.renderStreamMessage(html)
        }
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: 'Guardado', type: 'notice' }
        }))
        return true
      } else {
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: 'Error al guardar', type: 'alert' }
        }))
        return false
      }
    } catch (error) {
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: 'Error al guardar', type: 'alert' }
      }))
      return false
    }
  }
}
